import React, { useEffect, useState } from "react";
import { ListRequest } from "./crud";
import { defineMessage, FormattedMessage, MessageDescriptor } from "react-intl";
import TitledDialog from "../components/TitledDialog";
import { FetchFunction, useFetch } from "../components/FetchProvider";
import { fetchLists } from "./crud";
import Loading from "../components/Loading";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { Alert, Box, Button, MenuItem, Select, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { ClientInformation, collectClientRoles } from "./roleContext";

async function loadRelations(fetch: FetchFunction) {
    const requests = [
        new ListRequest("client", { name: "display_name" }),
        new ListRequest("role"),
        new ListRequest("role_context"),
        new ListRequest("context"),
    ];

    const results = await fetchLists(fetch, requests);
    const clients = results[0].rows;
    const roles = results[1].rows;
    console.log("relations", clients, roles);
    console.log("add relations", results[2].rows, results[3].rows);
    const clientsData: Record<number, ClientInformation> = {};
    clients.forEach((client) => {
        const clientRoles = collectClientRoles(
            client.id,
            roles,
            results[2].rows,
            results[3].rows,
            // do not allow creating user or client roles for heimdall-client
            client.display_name === "heimdall-client" ? ["user", "client"] : [],
        );

        if (Object.keys(clientRoles).length > 0) {
            clientsData[client.id] = {
                id: client.id,
                display_name: client.display_name,
                roles: clientRoles,
            };
        }
    });
    return clientsData;
}

const nullDefaultValues = { client: 0, role: 0, context: "" };
function getDefaultValues(clients: Record<number, ClientInformation>) {
    const clientIds = Object.keys(clients).map(Number);
    if (clientIds.length == 0) {
        return nullDefaultValues;
    }

    const defaultClient = clientIds[0];

    return {
        client: defaultClient,
        role: Object.keys(clients[defaultClient].roles).map(Number)[0],
        context: "",
    };
}

const AddRoleDialogForm: React.FC<{
    userSubId: number;
    onDirtyChange?: (isDirty: boolean) => void;
    onSuccess?: () => void;
}> = ({ userSubId, onDirtyChange, onSuccess }) => {
    const fetch = useFetch();
    const [clientsInfo, setClientsInfo] = useState<Record<number, ClientInformation>>(null);
    const isMountedRef = React.useRef(true);
    const [serverError, setServerError] = useState<MessageDescriptor | null>(null);

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { isDirty },
    } = useForm({
        defaultValues: nullDefaultValues,
        mode: "onChange",
    });

    const formValues = watch();

    // Report dirty state changes up to parent
    useEffect(() => {
        onDirtyChange?.(isDirty);

        return () => {
            isMountedRef.current = false;
        };
    }, [isDirty, onDirtyChange]);

    useEffect(() => {
        loadRelations(fetch).then((clientsData) => {
            setClientsInfo(clientsData);

            const defaultValues = getDefaultValues(clientsData);
            console.log("set", clientsData, defaultValues);
            reset(defaultValues);
        });
    }, [fetch, reset]);

    if (clientsInfo === null) {
        return <Loading />;
    }

    const onSubmit: SubmitHandler<any> = async (data) => {
        try {
            const contexts = [...currentRole.predefinedContexts];
            if (currentRole.contextField) {
                contexts.push({
                    id: currentRole.contextField.id,
                    data: {
                        [currentRole.contextField.label]: data.context,
                    },
                });
            }
            await fetch("/api/add-role", {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    role_id: data.role,
                    user_sub_id: userSubId,
                    contexts,
                }),
            });

            onSuccess();
        } catch (error) {
            setServerError(
                defineMessage({
                    id: "error.add_role_failed",
                    defaultMessage: "Failed to add role. Please try again.",
                }),
            );
        }
    };

    const hasErrors = false;

    console.log("formValues", formValues);

    const currentRole = clientsInfo[formValues.client]?.roles[formValues.role];

    return (
        <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)} maxWidth="60ch">
            <Grid container spacing={2} alignItems="center">
                <Grid size={4}>
                    <FormattedMessage id="client.client" defaultMessage="Client" />
                </Grid>
                <Grid size={8}>
                    <Controller
                        control={control}
                        name="client"
                        render={({ field }) => {
                            return (
                                <Select
                                    {...field}
                                    SelectDisplayProps={
                                        { "data-field": "client" } as React.HTMLAttributes<HTMLDivElement>
                                    }
                                    size="small"
                                    fullWidth
                                >
                                    {Object.values(clientsInfo).map((row) => (
                                        <MenuItem key={row.id} value={row.id} data-client-id={row.id}>
                                            {row.display_name}
                                        </MenuItem>
                                    ))}
                                    {field.value === 0 && <MenuItem key={0} value={0}></MenuItem>}
                                </Select>
                            );
                        }}
                    />
                </Grid>

                <Grid size={4}>
                    <FormattedMessage id="user_roles.role" defaultMessage="Role" />
                </Grid>
                <Grid size={8}>
                    <Controller
                        control={control}
                        name="role"
                        render={({ field }) => {
                            if (!(formValues.client in clientsInfo)) return null;

                            const clientRoles = clientsInfo[formValues.client]?.roles || {};
                            console.log("clientRoles", clientRoles);

                            // Ensure we have a valid role value for this client
                            const fieldValue =
                                field.value in clientRoles ? field.value : Object.keys(clientRoles).map(Number)[0];
                            console.log("fieldValue", fieldValue);

                            // Silently update form value if it's invalid
                            if (field.value !== fieldValue) {
                                setTimeout(() => field.onChange(fieldValue), 0);
                            }
                            return (
                                <Select
                                    {...field}
                                    value={fieldValue} // Use validated value
                                    SelectDisplayProps={
                                        { "data-field": "role" } as React.HTMLAttributes<HTMLDivElement>
                                    }
                                    size="small"
                                    fullWidth
                                >
                                    {Object.values(clientRoles).map((role) => {
                                        return (
                                            <MenuItem key={role.id} value={role.id} data-role-id={role.id}>
                                                {role.name}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            );
                        }}
                    />
                </Grid>

                <Grid size={4} sx={{ display: currentRole?.contextField ? "block" : "none" }}>
                    {/* eslint-disable-next-line formatjs/enforce-default-message, formatjs/enforce-id */}
                    {currentRole?.contextField && <FormattedMessage id={currentRole?.contextField.label} />}
                </Grid>
                <Grid size={8} sx={{ display: currentRole?.contextField ? "block" : "none" }}>
                    <Controller
                        control={control}
                        name="context"
                        render={({ field }) => <TextField {...field} size="small" fullWidth />}
                    />
                </Grid>

                {serverError && (
                    <Grid size={12} justifyContent="flex-end">
                        <Alert severity="error">
                            <FormattedMessage {...serverError} />
                        </Alert>
                    </Grid>
                )}
                <Grid size={12} container justifyContent="flex-end" spacing={1}>
                    <Grid>
                        <Button type="submit" disabled={hasErrors}>
                            <FormattedMessage id="form.save" defaultMessage="Save" />
                        </Button>
                    </Grid>
                    <Grid>
                        <Button onClick={() => reset()} disabled={!isDirty}>
                            <FormattedMessage id="form.reset" defaultMessage="Reset" />
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    );
};

const AddRoleDialog: React.FC<{
    userSubId: number;
    visible: boolean;
    onHide: (changed: boolean) => void;
}> = ({ userSubId, visible, onHide }) => {
    const [isFormDirty, setIsFormDirty] = React.useState(false);

    return (
        <TitledDialog
            open={visible}
            hide={() => onHide(false)}
            isContentChanged={() => isFormDirty}
            title="user_roles.create"
            dialogLabel="role-grant-create"
        >
            <AddRoleDialogForm userSubId={userSubId} onSuccess={() => onHide(true)} onDirtyChange={setIsFormDirty} />
        </TitledDialog>
    );
};

export default AddRoleDialog;
